import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { getNutritionFoods, getFoodPortions } from '../api';

// Shared food-search + quantity-selection UI — originally built for the player's Food Tracker
// (PlayerNutrition.js), extracted here so the chef recipe editor (RecipeEditor.js) can reuse
// the EXACT same NEVO/alias search and Portie-online-backed quantity picker for choosing a
// recipe ingredient's amount, instead of a second implementation. Nothing about the behavior
// changed in this extraction — PlayerNutrition.js now imports these instead of defining them.
export const round1 = n => Math.round(n * 10) / 10;

// The kitchen's three fixed physical scoops — shared between RecipeEditor.js (choosing one per
// scoop-station group) and ChefWeekPlanner.js's admin overview of the same thing across many
// recipes, so both ALWAYS show the identical name/order. Named by their real physical
// appearance rather than the bare ml figure, since that's what a chef actually recognises on the
// shelf — the ml stays alongside, in parentheses, purely for reference/precision, but the name is
// what's read first everywhere a scoop is chosen or shown.
export const SCOOP_SIZES = [
  { ml: 118, name: 'kleine groene lepel' },
  { ml: 177.3, name: 'soeplepel' },
  { ml: 237, name: 'grote blauwe lepel' },
];
export function scoopSizeLabel(ml) {
  const s = SCOOP_SIZES.find(x => x.ml === ml);
  return s ? `${s.name} (${ml} ml)` : `${ml} ml`;
}

// "Aardappel (Potato)" — every search row shows both names together so nobody has to guess
// whether an English search term and a Dutch food name are the same thing. Skipped when
// there's no distinct English name (e.g. barcode-scanned foods).
export function pairedName(food) {
  const en = food.name_en && food.name_en.trim();
  if (!en || en.toLowerCase() === food.name.toLowerCase()) return food.name;
  return `${food.name} (${en})`;
}

const inputStyle = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none' };
const btn = (active, color = '#3b82f6') => ({
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-strong)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
const linkBtn = { background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 11, padding: 0, fontFamily: 'inherit' };
const scanTag = { fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '1px 4px', borderRadius: 4, marginLeft: 6, color: '#3b82f6', border: '1px solid rgba(59,130,246,0.4)' };
const photoTag = { ...scanTag, color: '#a855f7', border: '1px solid rgba(168,85,247,0.4)' };
const chefTag = { ...scanTag, color: '#10b981', border: '1px solid rgba(16,185,129,0.4)' };
const playerTag = { ...scanTag, color: '#f59e0b', border: '1px solid rgba(245,158,11,0.4)' };
// 'off' = barcode matched Open Food Facts; 'photo' = barcode had no OFF match and someone
// photographed + confirmed the label instead; 'chef'/'player' = added with no barcode at all
// (manually or via the same photo-read flow), tagged by who added it. All count fully right
// away everywhere — this tag is purely provenance/transparency, never a trust gate.
export function SourceTag({ source }) {
  if (source === 'off') return <span style={scanTag}>scan</span>;
  if (source === 'photo') return <span style={photoTag}>photo</span>;
  if (source === 'chef') return <span style={chefTag}>chef</span>;
  if (source === 'player') return <span style={playerTag}>player</span>;
  // Not a real nutrition_foods.source value — server.staging.js's computeLoggedScoopEntries
  // tags a player's logged scoop/unit count from the chef's planned meal with this, so it reads
  // as a diary line but is visually distinct from a food the player added themselves.
  if (source === 'chef_meal') return <span style={photoTag}>meal plan</span>;
  return null;
}

// ── Food search / autocomplete (matches name + aliases server-side) ──────────
export function FoodAutocomplete({ onPick, placeholder = 'Search food…' }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults([]); return; }
      try { setResults((await getNutritionFoods(q.trim())) || []); setOpen(true); }
      catch { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={13} color="var(--text-dimmed)" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)' }} />
        <input value={q} onChange={e => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder} style={{ ...inputStyle, paddingLeft: 28 }} />
      </div>
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '105%', left: 0, right: 0, zIndex: 50, background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, maxHeight: 240, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
          {results.map(f => (
            <button key={f.id} type="button"
              onClick={() => { onPick(f); setQ(''); setResults([]); setOpen(false); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
              {pairedName(f)}<SourceTag source={f.source} />
              <span style={{ color: 'var(--text-dimmed)', marginLeft: 6, fontSize: 11 }}>
                {Math.round(f.kcal_per_100g)} kcal/100g{f.grams_per_portion != null ? ` · portion ${Math.round(f.grams_per_portion)} g` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Quantity picker — dispatches to one of three sub-pickers depending on what this food
// actually has, then a free-grams field as the universal fallback in every one of them:
//  - NamedPortionPicker: the food has real Portie-online measures ("1 handje" 48g, "1 tros"
//    500g, ...) — the normal case for NEVO foods with a Portie-online match.
//  - SinglePortionPicker: no named measures, but the food has its own ONE real serving size —
//    a barcode/photo-scanned product's printed package serving.
//  - PlainGramsPicker: neither — a NEVO food with no Portie-online match (whether or not the
//    NEVO import's AI made a rough portion_g guess for it: a guess is not a real named measure,
//    so it's deliberately never offered as one here — just "100g" as a reference + free grams).
// `food` is polymorphic: a freshly picked search/scan result (id, source, grams_per_portion) OR
// an existing diary line/recipe ingredient being edited (food_id, food_source, grams_per_portion).
export function QuantityPicker({ food, initialGrams = null, onChange }) {
  const foodId = food.food_id ?? food.id;
  const source = food.source ?? food.food_source;
  const [portions, setPortions] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    setPortions(null);
    if (!foodId) { setPortions([]); return; }
    getFoodPortions(foodId).then(list => { if (!cancelled) setPortions(list || []); })
      .catch(() => { if (!cancelled) setPortions([]); });
    return () => { cancelled = true; };
  }, [foodId]);

  if (portions === null) {
    // Loading portions shouldn't block typing a grams value straight away.
    return <PlainGramsPicker initialGrams={initialGrams} onChange={onChange} />;
  }
  if (portions.length > 0) {
    return <NamedPortionPicker portions={portions} initialGrams={initialGrams} onChange={onChange} />;
  }
  const per = food.grams_per_portion != null ? Number(food.grams_per_portion) : null;
  if (['off', 'photo', 'chef', 'player'].includes(source) && per > 0) {
    return <SinglePortionPicker per={per} initialGrams={initialGrams} onChange={onChange} />;
  }
  return <PlainGramsPicker initialGrams={initialGrams} onChange={onChange} showHundredHint />;
}

// Free grams entry — the universal fallback, and the only picker for a NEVO food with no
// Portie-online measures. `showHundredHint` shows a "100g" quick-fill (the value the food's
// nutrition is expressed per) rather than inventing a fake named portion for it.
function PlainGramsPicker({ initialGrams, onChange, showHundredHint = false }) {
  const [grams, setGrams] = useState(initialGrams != null ? String(initialGrams) : '');
  // `onChange` is a fresh inline arrow function on every render of the caller (RecipeEditor
  // creates `g => setAmount(x.key, g)` inline in JSX) — an effect that lists `onChange` itself
  // as a dependency therefore re-fires on EVERY parent render, not just when `grams` changes:
  // it calls onChange -> parent setState -> parent re-renders -> a NEW onChange reference ->
  // effect deps look "changed" again -> fires again, forever. That infinite loop was the real
  // bug (confirmed by actually running the component and watching it: 12MB+ of repeated render
  // logs from a single mount) — not the missing-resync theories from the first two attempts. A
  // ref holds the latest onChange without being a reactive dependency, so the push-effect below
  // only depends on `grams` itself.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });
  useEffect(() => { onChangeRef.current(Number(grams) > 0 ? Number(grams) : null); }, [grams]);
  // initialGrams is normally only read once, at mount — but a caller can also push a NEW amount
  // onto an already-mounted picker for the SAME food (RecipeEditor's raw-vs-cooked "use
  // converted amount" button changes food AND amount together; QuantityPicker's own key change
  // handles the food swap, but this input's `grams` state still needs to actually pick up the
  // new number rather than silently keep whatever was typed/shown before).
  useEffect(() => { setGrams(initialGrams != null ? String(initialGrams) : ''); }, [initialGrams]);
  return (
    <div>
      <input type="number" min="0" step="1" value={grams} onChange={e => setGrams(e.target.value)}
        placeholder="grams" style={{ ...inputStyle, width: 120 }} autoFocus />
      {showHundredHint && (
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 6 }}>
          No standard portion for this food —{' '}
          <button style={linkBtn} onClick={() => setGrams('100')}>use 100 g</button> or enter your own amount.
        </div>
      )}
    </div>
  );
}

// A single real serving size (barcode/photo-scanned foods). New entry (initialGrams == null):
// starts on portion buttons. Editing an existing entry: starts on grams so the exact stored
// value shows and isn't silently rounded to a portion.
function SinglePortionPicker({ per, initialGrams, onChange }) {
  const [mode, setMode] = useState(initialGrams == null ? 'portion' : 'grams');
  const [portions, setPortions] = useState(() => {
    if (initialGrams) {
      const p = Math.round((initialGrams / per) * 2) / 2;
      return p > 0 ? p : 1;
    }
    return 1;
  });
  const [grams, setGrams] = useState(initialGrams != null ? String(initialGrams) : '');
  // See PlainGramsPicker's identical fix — `onChange` must NOT be a dependency of the
  // value-pushing effect below (a fresh inline function every parent render turns it into an
  // infinite loop), so its latest value is read through a ref instead.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });
  // See PlainGramsPicker's identical effect — keeps this field in sync with an amount pushed in
  // from outside after mount (e.g. the raw-vs-cooked conversion), not just what's typed here.
  useEffect(() => { setGrams(initialGrams != null ? String(initialGrams) : ''); }, [initialGrams]);

  useEffect(() => {
    if (mode === 'portion') onChangeRef.current(round1(portions * per));
    else onChangeRef.current(Number(grams) > 0 ? Number(grams) : null);
  }, [mode, portions, grams, per]);

  if (mode === 'grams') {
    return (
      <div>
        <input type="number" min="0" step="1" value={grams} onChange={e => setGrams(e.target.value)}
          placeholder="grams" style={{ ...inputStyle, width: 120 }} autoFocus />
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 6 }}>
          <button style={linkBtn} onClick={() => setMode('portion')}>use portions instead</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {[1, 2, 3].map(n => (
          <button key={n} style={btn(portions === n)} onClick={() => setPortions(n)}>
            {n} portion{n > 1 ? 's' : ''}
          </button>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
          <button style={{ ...btn(false), padding: '4px 9px' }} onClick={() => setPortions(p => Math.max(0.5, round1(p - 0.5)))}>−</button>
          <span style={{ fontSize: 13, minWidth: 34, textAlign: 'center', color: 'var(--text-primary)' }}>{portions}×</span>
          <button style={{ ...btn(false), padding: '4px 9px' }} onClick={() => setPortions(p => round1(p + 0.5))}>+</button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 6 }}>
        ≈ {round1(portions * per)} g · {Math.round(per)} g per portion ·{' '}
        <button style={linkBtn} onClick={() => setMode('grams')}>enter grams</button>
      </div>
    </div>
  );
}

// Labelled buttons for every stored measure ("1 handje" 48g, "1 tros" 500g, ...), a
// ×multiplier for "2 boterhammen" etc., and a free-grams fallback. Final grams = selected
// measure's grams × multiplier, rounded to 1 decimal. Editing an existing entry starts on grams
// (same reasoning as SinglePortionPicker) — reverse-guessing which measure×multiplier
// combination originally produced a stored gram amount isn't reliable enough to silently
// reconstruct, so the exact value is shown as-is instead.
function NamedPortionPicker({ portions, initialGrams, onChange }) {
  const defaultIdx = Math.max(0, portions.findIndex(p => p.is_default));
  const [mode, setMode] = useState(initialGrams == null ? 'measure' : 'grams');
  const [selectedIdx, setSelectedIdx] = useState(defaultIdx);
  const [multiplier, setMultiplier] = useState(1);
  const [grams, setGrams] = useState(initialGrams != null ? String(initialGrams) : '');
  const selected = portions[selectedIdx];
  // See PlainGramsPicker's identical fix — `onChange` must NOT be a dependency of the
  // value-pushing effect below (a fresh inline function every parent render turns it into an
  // infinite loop), so its latest value is read through a ref instead.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });
  // See PlainGramsPicker's identical effect — keeps this field in sync with an amount pushed in
  // from outside after mount (e.g. the raw-vs-cooked conversion), not just what's typed here.
  // Deliberately only resyncs `grams`, not `mode` — a chef actively using measure/portion mode
  // shouldn't get bounced back to grams mode just because picking a measure changes the amount.
  useEffect(() => { setGrams(initialGrams != null ? String(initialGrams) : ''); }, [initialGrams]);

  useEffect(() => {
    if (mode === 'measure' && selected) onChangeRef.current(round1(Number(selected.grams) * multiplier));
    else onChangeRef.current(Number(grams) > 0 ? Number(grams) : null);
  }, [mode, selectedIdx, multiplier, grams, selected]);

  if (mode === 'grams') {
    return (
      <div>
        <input type="number" min="0" step="1" value={grams} onChange={e => setGrams(e.target.value)}
          placeholder="grams" style={{ ...inputStyle, width: 120 }} autoFocus />
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 6 }}>
          <button style={linkBtn} onClick={() => setMode('measure')}>choose a measure instead</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {portions.map((p, i) => (
          <button key={p.id ?? i} style={btn(i === selectedIdx)} onClick={() => setSelectedIdx(i)}>
            {p.label} <span style={{ opacity: 0.75 }}>({Math.round(p.grams)} g)</span>
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8 }}>
        <button style={{ ...btn(false), padding: '4px 9px' }} onClick={() => setMultiplier(m => Math.max(0.5, round1(m - 0.5)))}>−</button>
        <span style={{ fontSize: 13, minWidth: 34, textAlign: 'center', color: 'var(--text-primary)' }}>{multiplier}×</span>
        <button style={{ ...btn(false), padding: '4px 9px' }} onClick={() => setMultiplier(m => round1(m + 0.5))}>+</button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 6 }}>
        ≈ {selected ? round1(Number(selected.grams) * multiplier) : 0} g ·{' '}
        <button style={linkBtn} onClick={() => setMode('grams')}>enter grams</button>
      </div>
    </div>
  );
}
